import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine } from 'recharts'
import { BarChart3, TrendingUp, User, Package, Truck } from 'lucide-react'
import { Vehicle } from '../types'
import { apiClient } from '../config/api'
import { DateRange, todayRange } from './CalendarModal'

interface SpeedChartProps {
  vehicle: Vehicle | null
  timeRange?: number
  onTimelineHover?: (timestamp: string | null, position: { lat: number, lng: number } | null) => void
  dateRange?: DateRange // Accept date range from parent
  /** Dock mode: no vehicle-identity header, slim stats row, flex-based height */
  compact?: boolean
}

interface SpeedDataPoint {
  time: string
  speed: number
  timestamp: Date
  formattedTime: string
  latitude?: number
  longitude?: number
}

const SpeedChart: React.FC<SpeedChartProps> = ({ vehicle, onTimelineHover, dateRange: propDateRange, compact = false }) => {
  const { t } = useTranslation()
  const [speedData, setSpeedData] = useState<SpeedDataPoint[]>([])
  const [loading, setLoading] = useState(false)
  const [hoveredPoint, setHoveredPoint] = useState<SpeedDataPoint | null>(null)
  
  // Use date range from props or default
  const dateRange = propDateRange || todayRange()


  useEffect(() => {
    if (vehicle && dateRange.startDate && dateRange.endDate) {
      // A rolling window carries exact instants; whole-day presets do not.
      fetchSpeedDataByDateRange(
        vehicle.id,
        dateRange.startAt || dateRange.startDate,
        dateRange.endAt || dateRange.endDate
      )
    } else {
      setSpeedData([])
    }
  }, [vehicle, dateRange])

  const fetchSpeedDataByDateRange = async (vehicleId: string, start: string, end: string) => {
    setLoading(true)
    try {
      console.log(`📊 Fetching speed data for ${vehicleId} from ${start} to ${end}`)
      
      // Calculate hours from date range
      const startTime = new Date(start).getTime()
      const endTime = new Date(end).getTime()
      const hours = Math.ceil((endTime - startTime) / (1000 * 60 * 60))
      
      // Use timeline endpoint with full date range
      console.log(`⏰ Requesting ${hours} hours of speed data for full date range`)
      
      const response = await apiClient.get(`/timeline/${vehicleId}`, { 
        startDate: start,
        endDate: end,
        hours: hours
      })
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`📊 TIMELINE DATA RECEIVED:`, response.data.length, 'points')
        
        const data: SpeedDataPoint[] = response.data
          .map((point: any) => {
            const timestamp = new Date(point.timestamp)
            return {
              time: timestamp.toISOString(),
              speed: point.speed || 0,
              timestamp,
              formattedTime: timestamp.toLocaleTimeString('fr-FR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              }),
              latitude: parseFloat(point.location?.latitude || 0),
              longitude: parseFloat(point.location?.longitude || 0)
            }
          })
          .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

        setSpeedData(data)
        console.log(`✅ ALL SPEED DATA LOADED: ${data.length} points`)
        console.log(`✅ Speed data processed successfully`)
      }
    } catch (error) {
      console.error('❌ Error fetching ALL speed data:', error)
      setSpeedData([])
    } finally {
      setLoading(false)
    }
  }



  const getMaxSpeed = () => {
    return Math.max(...speedData.map(d => d.speed), 100)
  }

  const getAverageSpeed = () => {
    if (speedData.length === 0) return 0
    return speedData.reduce((sum, d) => sum + d.speed, 0) / speedData.length
  }

  // Handle mouse move over chart
  const handleMouseMove = useCallback((data: any) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const point = data.activePayload[0].payload as SpeedDataPoint
      setHoveredPoint(point)
      
      // Notify parent component with position for map interaction
      if (onTimelineHover && point.latitude && point.longitude) {
        onTimelineHover(point.time, { lat: point.latitude, lng: point.longitude })
      }
    }
  }, [onTimelineHover])

  // Handle mouse leave chart
  const handleMouseLeave = useCallback(() => {
    setHoveredPoint(null)
    if (onTimelineHover) {
      onTimelineHover(null, null)
    }
  }, [onTimelineHover])

  if (!vehicle) {
    return (
      <div className={`h-full bg-white ${compact ? '' : 'border-t'}`}>
        <div className="flex items-center justify-center h-full text-gray-500">
          <div className="text-center">
            <BarChart3 size={22} className="mx-auto mb-2 text-gray-400" />
            <p>{t('select_vehicle')}</p>
            <p className="text-sm">{t('speed_select_hint')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`h-full bg-white flex flex-col min-h-0 ${compact ? '' : 'border-t'}`}>
      {/* Header: full vehicle identity only outside compact/dock mode */}
      <div className="px-3 py-2 border-b bg-gray-50 shrink-0">
        {!compact && (
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-semibold text-gray-800">{vehicle.name}</h3>
              <div className="text-sm text-gray-600">{t('speed_analysis')}</div>
              <div className="text-xs text-gray-500">
                {vehicle.identifierType || 'ID'}: {vehicle.plateNumber}
              </div>
              {/* Show customer/container context */}
              {vehicle.customerInfo?.name && (
                <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                  <User size={12} />{vehicle.customerInfo.name}
                </div>
              )}
              {vehicle.containerInfo?.name && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <Package size={12} />{t('container')}: {vehicle.containerInfo.name}
                </div>
              )}
              {vehicle.assetInfo?.code && (
                <div className="flex items-center gap-1 text-xs text-purple-600">
                  <Truck size={12} />{t('asset')}: {vehicle.assetInfo.type} {vehicle.assetInfo.code}
                </div>
              )}
            </div>

            {/* Date range controlled by parent component */}
            <div className="text-xs text-gray-500">
              {t('period_label')}: {dateRange.label || `${new Date(dateRange.startDate).toLocaleDateString('en-GB')} - ${new Date(dateRange.endDate).toLocaleDateString('en-GB')}`}
            </div>
          </div>
        )}

        {/* Speed statistics */}
        <div className={`grid grid-cols-4 gap-2 text-center ${compact ? 'py-0.5' : ''}`}>
          <div>
            <div className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-lg'}`}>{vehicle.speed}</div>
            <div className="text-xs text-gray-500">{t('current_speed')}</div>
          </div>
          <div>
            <div className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-lg'}`}>
              {getAverageSpeed().toFixed(1)}
            </div>
            <div className="text-xs text-gray-500">{`${t('kmh')} ${t('avg_short')}`}</div>
          </div>
          <div>
            <div className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-lg'}`}>
              {speedData.length > 0 ? Math.max(...speedData.map(d => d.speed)).toFixed(1) : '0'}
            </div>
            <div className="text-xs text-gray-500">{`${t('kmh')} max`}</div>
          </div>
          <div>
            <div className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-lg'}`}>{speedData.length}</div>
            <div className="text-xs text-gray-500">{t('data_points')}</div>
          </div>
        </div>
      </div>

      {/* Chart area */}
      <div className={`flex-1 min-h-0 ${compact ? 'p-1.5' : 'p-3'}`}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : speedData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={speedData}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="formattedTime"
                tick={{ fontSize: compact ? 10 : 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[0, getMaxSpeed()]}
                tick={{ fontSize: compact ? 10 : 12 }}
                width={compact ? 34 : undefined}
                label={compact ? undefined : { value: `${t('speed')} (${t('kmh')})`, angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                labelFormatter={(value) => `${t('time')}: ${value}`}
                formatter={(value: number, _name: string, props: any) => {
                  const point = props.payload as SpeedDataPoint
                  const locationText = point.latitude && point.longitude ? 
                    ` | ${point.latitude.toFixed(4)}, ${point.longitude.toFixed(4)}` : ''
                  return [`${value.toFixed(1)} ${t('kmh')}${locationText}`, t('speed')]
                }}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              />
              <defs>
                <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="speed"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#speedGradient)"
              />
              {/* Speed limit lines */}
              <Line 
                type="monotone" 
                dataKey={() => 50} 
                stroke="#f59e0b" 
                strokeDasharray="5 5" 
                strokeWidth={1}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey={() => 90} 
                stroke="#ef4444" 
                strokeDasharray="5 5" 
                strokeWidth={1}
                dot={false}
              />
              {/* Hover indicator line */}
              {hoveredPoint && (
                <ReferenceLine 
                  x={hoveredPoint.formattedTime} 
                  stroke="#ff6b6b" 
                  strokeWidth={2}
                  strokeDasharray="2 2"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <TrendingUp size={22} className="mx-auto mb-2 text-gray-400" />
              <p>{t('speed_no_data')}</p>
              <p className="text-sm">{t('for_selected_period')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      {speedData.length > 0 && (
        <div className={`shrink-0 border-t bg-gray-50 ${compact ? 'px-2 py-1' : 'px-4 py-2'}`}>
          <div className="flex items-center justify-center space-x-6 text-xs">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-1 bg-blue-500"></div>
              <span>{t('speed_actual')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-1 bg-yellow-500" style={{ borderTop: '1px dashed' }}></div>
              <span>{`50 ${t('kmh')}`}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-1 bg-red-500" style={{ borderTop: '1px dashed' }}></div>
              <span>{`90 ${t('kmh')}`}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SpeedChart
